<script setup>
import { ref, computed, reactive, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, requiredIf, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');
const flows = useMapGetter('flows/getFlows');
const router = useRouter();

const formState = reactive({
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  botType: 'ai_agent',
  flowId: '',
  productName: '',
});

// Every brain the runtime knows, in the same order the list filters them.
const BRAIN_HINTS = {
  ai_agent: 'AGENT_BOTS.BRAIN_HINT.AI_AGENT',
  flow: 'AGENT_BOTS.BRAIN_HINT.FLOW',
  webhook: 'AGENT_BOTS.BRAIN_HINT.WEBHOOK',
  remote: 'AGENT_BOTS.BRAIN_HINT.REMOTE',
};

const botTypeOptions = computed(() => [
  { value: 'ai_agent', label: t('AGENT_BOTS.TYPES.AI_AGENT') },
  { value: 'flow', label: t('AGENT_BOTS.TYPES.FLOW') },
  { value: 'webhook', label: t('AGENT_BOTS.TYPES.WEBHOOK') },
  { value: 'remote', label: t('AGENT_BOTS.TYPES.REMOTE') },
]);

// A URL is what a webhook bot and a remote brain both need, and what neither of the other two has.
const needsUrl = computed(() =>
  ['webhook', 'remote'].includes(formState.botType)
);

const brainHint = computed(() => t(BRAIN_HINTS[formState.botType]));

const publishedFlowOptions = computed(() =>
  flows.value
    .filter(flow => flow.status === 'published')
    .map(flow => ({ value: flow.id, label: flow.name }))
);

onMounted(() => {
  store.dispatch('flows/get');
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');
const botSecret = ref('');

// An AI agent is created whole here - the agent and the bot it runs in - and opens on its own page,
// where its instructions, knowledge and inboxes live. Editing one happens there, not in this dialog.
const isNewAgent = computed(
  () => props.type === MODAL_TYPES.CREATE && formState.botType === 'ai_agent'
);

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
    botUrl: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.URL'),
        requiredIf(() => needsUrl.value)
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
        url
      ),
    },
    flowId: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FLOW'),
        requiredIf(() => formState.botType === 'flow')
      ),
    },
    productName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.PRODUCT'),
        requiredIf(() => isNewAgent.value)
      ),
    },
  },
  formState
);

const isCreatingAgent = ref(false);

const createAgent = async () => {
  isCreatingAgent.value = true;
  try {
    const assistant = await store.dispatch('captainAssistants/create', {
      name: formState.botName,
      description: formState.botDescription,
      config: { product_name: formState.productName },
    });
    await store.dispatch('agentBots/get');
    const bot = store.getters['agentBots/getBots'].find(
      item => Number(item.bot_config?.assistant_id) === assistant.id
    );
    if (bot && formState.botAvatar) {
      await store.dispatch('agentBots/update', {
        id: bot.id,
        data: { avatar: formState.botAvatar },
      });
    }
    useAlert(t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
    router.push(
      frontendURL(
        `accounts/${router.currentRoute.value.params.accountId}/captain/${assistant.id}/settings`
      )
    );
  } catch (error) {
    useAlert(t('AGENT_BOTS.ADD.API.ERROR_MESSAGE'));
  } finally {
    isCreatingAgent.value = false;
  }
};

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating || isCreatingAgent.value
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);

const productNameError = computed(() =>
  v$.value.productName.$error ? v$.value.productName.$errors[0]?.$message : ''
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
    botType: 'ai_agent',
    flowId: '',
    productName: '',
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;
  if (isNewAgent.value) {
    await createAgent();
    return;
  }

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: needsUrl.value ? formState.botUrl : '',
    bot_type: formState.botType,
    flow_id: formState.botType === 'flow' ? formState.flowId : '',
    avatar: formState.botAvatar,
  };

  // Editing keeps the agent behind an AI bot: that is what the runtime resolves the brain from.
  if (formState.botType === 'ai_agent') {
    botData.bot_config = props.selectedBot?.bot_config || {};
  }

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    // Show access token and secret after creation
    if (isCreate) {
      const {
        access_token: responseAccessToken,
        secret: responseSecret,
        id,
      } = response || {};

      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        botSecret.value = responseSecret || '';
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        botSecret.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      secret: botSecretValue,
      bot_type: botType,
      flow_id: flowId,
    } = props.selectedBot;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || botConfig?.webhook_url || '';
    formState.botAvatarUrl = thumbnail || '';
    formState.botType = botType || 'webhook';
    formState.flowId = flowId || '';

    if (props.type === MODAL_TYPES.EDIT) {
      if (botAccessToken) accessToken.value = botAccessToken;
      if (botSecretValue) botSecret.value = botSecretValue;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onCopySecret = async value => {
  await copyTextToClipboard(value || botSecret.value);
  useAlert(t('AGENT_BOTS.SECRET.COPY_SUCCESS'));
};

const onResetSecret = async () => {
  const response = await store.dispatch(
    'agentBots/resetSecret',
    props.selectedBot.id
  );
  if (response) {
    botSecret.value = response.secret;
    useAlert(t('AGENT_BOTS.SECRET.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.SECRET.RESET_ERROR'));
  }
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  botSecret.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="closeModal"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div
        v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
        class="flex flex-col gap-4"
      >
        <div class="mb-2 flex flex-col items-start">
          <span class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
          </span>
          <Avatar
            :src="formState.botAvatarUrl"
            :name="formState.botName"
            :size="68"
            allow-upload
            icon-name="i-lucide-bot-message-square"
            @upload="handleImageUpload"
            @delete="handleAvatarDelete"
          />
        </div>

        <Input
          id="bot-name"
          v-model="formState.botName"
          :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
          :message="botNameError"
          :message-type="botNameError ? 'error' : 'info'"
          @blur="v$.botName.$touch()"
        />

        <TextArea
          id="bot-description"
          v-model="formState.botDescription"
          :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.TYPE.LABEL') }}
          </label>
          <ComboBox v-model="formState.botType" :options="botTypeOptions" />
          <p class="text-xs text-n-slate-11">{{ brainHint }}</p>
        </div>

        <Input
          v-if="needsUrl"
          id="bot-url"
          v-model="formState.botUrl"
          :label="
            formState.botType === 'remote'
              ? $t('AGENT_BOTS.FORM.REMOTE_URL.LABEL')
              : $t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')
          "
          :placeholder="
            formState.botType === 'remote'
              ? $t('AGENT_BOTS.FORM.REMOTE_URL.PLACEHOLDER')
              : $t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')
          "
          :message="botUrlError"
          :message-type="botUrlError ? 'error' : 'info'"
          @blur="v$.botUrl.$touch()"
        />

        <Input
          v-else-if="formState.botType === 'ai_agent'"
          v-model="formState.productName"
          :label="$t('AGENT_BOTS.FORM.PRODUCT.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.PRODUCT.PLACEHOLDER')"
          :message="productNameError || $t('AGENT_BOTS.FORM.PRODUCT.HELP')"
          :message-type="productNameError ? 'error' : 'info'"
          @blur="v$.productName.$touch()"
        />

        <div v-else class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.FLOW.LABEL') }}
          </label>
          <ComboBox
            v-model="formState.flowId"
            :options="publishedFlowOptions"
            :placeholder="$t('AGENT_BOTS.FORM.FLOW.PLACEHOLDER')"
            @blur="v$.flowId.$touch()"
          />
          <p
            v-if="!publishedFlowOptions.length"
            class="text-xs text-n-slate-11"
          >
            {{ $t('AGENT_BOTS.FORM.FLOW.NO_PUBLISHED_FLOWS') }}
          </p>
        </div>
      </div>

      <div
        v-if="botSecret && type === MODAL_TYPES.EDIT"
        class="flex flex-col gap-1"
      >
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.SECRET.LABEL') }}
        </label>
        <AccessToken
          :value="botSecret"
          @on-copy="onCopySecret"
          @on-reset="onResetSecret"
        />
      </div>

      <div v-if="showAccessTokenInput" class="flex flex-col gap-1">
        <label
          v-if="type === MODAL_TYPES.EDIT"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
        </label>
        <AccessToken
          v-if="type === MODAL_TYPES.EDIT"
          :value="accessToken"
          @on-copy="onCopyToken"
          @on-reset="onResetToken"
        />
        <AccessToken
          v-else
          :value="accessToken"
          :show-reset-button="false"
          @on-copy="onCopyToken"
        />
      </div>

      <div
        v-if="botSecret && showAccessToken && type === MODAL_TYPES.CREATE"
        class="flex flex-col gap-1"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('AGENT_BOTS.SECRET.CREATED_DESC') }}
        </p>
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.SECRET.LABEL') }}
        </label>
        <AccessToken
          :value="botSecret"
          :show-reset-button="false"
          @on-copy="onCopySecret"
        />
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
